import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import LocationResolver from "@/components/LocationResolver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function PlaceCreateDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [heroFileId, setHeroFileId] = useState(null);
  const [imgUrl, setImgUrl] = useState("");
  const [creating, setCreating] = useState(false);

  const reset = () => { setSelected(null); setHeroFileId(null); setImgUrl(""); };

  const create = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      const { data } = await api.post("/places", {
        name: selected.name, city: selected.city, region: selected.region, country: selected.country,
        lat: selected.lat, lng: selected.lng,
        hero_file_id: heroFileId || null, hero_image_url: imgUrl.trim() || null,
      });
      toast.success(t("places.createPlace", "Create Place"));
      reset(); onOpenChange(false); onCreated && onCreated(data);
    } catch (e) { toast.error(t("actionFailed", "Action failed")); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="place-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("places.newPlace", "New Place")}</DialogTitle>
          <DialogDescription>{t("places.selectResult", "Select the correct location")}</DialogDescription>
        </DialogHeader>

        <LocationResolver onConfirm={(pt) => { setSelected(pt); toast.success(t("places.resolved", "Location resolved")); }} />

        {selected && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-2" data-testid="place-confirmed">
            <div className="text-sm font-medium text-slate-800">{selected.name}</div>
            <div className="text-xs text-slate-500">{[selected.city, selected.region, selected.country].filter(Boolean).join(", ")}</div>
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">{t("places.heroImage", "Hero image")}</div>
              <FileUpload category="place" accept="image/*" label={t("places.uploadHero", "Upload / replace image")} onUploaded={(d) => { setHeroFileId(d.id); setImgUrl(""); toast.success("OK"); }} />
              <Input value={imgUrl} onChange={(e) => { setImgUrl(e.target.value); setHeroFileId(null); }} placeholder={t("places.imageUrl", "Or paste image URL")} className="rounded-sm mt-2" data-testid="create-image-url" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="rounded-sm">{t("places.cancel", "Cancel")}</Button>
          <Button onClick={create} disabled={!selected || creating} data-testid="create-place-btn" className="rounded-sm bg-[#C5A059] hover:bg-[#b08d4a] text-white">
            {creating ? t("places.creating", "Creating…") : t("places.createPlace", "Create Place")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
